import React from "react";
import { Link } from "react-router-dom";
import { CustomerServiceSidebar } from "./Delivery";
import "../css/content-page.scss";
import "../css/terms.scss";

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

function Terms() {
  return (
    <main className="content-page terms-page">
      <div className="content-page__shell">
        <CustomerServiceSidebar currentPath="/terms" />

        <article className="content-page__article" aria-labelledby="terms-title">
          <header className="content-page__header">
            <p className="content-page__eyebrow">Storefront rules</p>
            <h1 id="terms-title">Terms &amp; Conditions</h1>
            <p className="content-page__lead">
              These terms govern access to the sickzuno storefront and orders placed
              through it. Related policies form part of these terms where applicable.
            </p>
          </header>

          <p className="content-page__meta">Last updated: October 16, 2026</p>

          <div className="content-page__notice" role="note">
            <strong>Please read before ordering:</strong> by submitting an order, you
            agree to these terms and the policies linked from the checkout and Policy
            Center. Nothing here removes a right that applicable law does not allow a
            business to exclude.
          </div>

          <section className="content-page__section" aria-labelledby="terms-acceptance">
            <h2 id="terms-acceptance">1. Acceptance and eligibility</h2>
            <p>
              By using this storefront, you agree to these terms. If you do not agree,
              do not use the site or submit an order. You must be legally able to enter
              the purchase or act with the involvement of a parent or guardian where
              required by law.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="terms-storefront">
            <h2 id="terms-storefront">2. Storefront information</h2>
            <p>
              We aim to present product names, images, options, weights, availability and
              descriptions accurately. Natural ingredients may vary in color, size,
              texture, aroma and flavor. A minor display or natural variation is not by
              itself a defect.
            </p>
            <p>
              The package label controls if it contains newer ingredient, allergen,
              preparation, storage or safety information than the website.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="terms-orders">
            <h2 id="terms-orders">3. Orders and contract formation</h2>
            <p>
              Adding an item to the cart does not reserve inventory. An acknowledgement
              confirms receipt of an order request, not final acceptance. An order is
              accepted when payment is approved and sickzuno confirms that it can be
              fulfilled. We may decline or cancel an order for the reasons explained in
              the{" "}
              <Link className="content-page__inline-link" to="/orders" onClick={scrollToTop}>
                Order &amp; Cancellation Policy
              </Link>
              .
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="terms-prices">
            <h2 id="terms-prices">4. Prices, promotions and taxes</h2>
            <p>
              Prices are shown in U.S. dollars unless stated otherwise. Shipping, tax,
              duty or other applicable charges may be displayed separately. Promotions
              are subject to their stated eligibility, timing and usage limits. Clear
              errors may be corrected before an order is accepted, with the customer
              given the choice to proceed or cancel where appropriate.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="terms-payment">
            <h2 id="terms-payment">5. Payment</h2>
            <p>
              You authorize the amount shown at checkout when you continue to the hosted
              payment page. Use only a payment method you are authorized to use. Card
              details are entered with the payment provider and are not collected by the
              sickzuno checkout form. Payment remains subject to provider and issuer
              approval.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="terms-shipping">
            <h2 id="terms-shipping">6. Shipping and cross-border orders</h2>
            <p>
              Delivery estimates are not guaranteed dates. Carrier events, weather,
              customs and an inaccurate address can cause delays. Customers are
              responsible for import eligibility and destination charges unless checkout
              expressly says those charges are included. See the{" "}
              <Link className="content-page__inline-link" to="/shipping" onClick={scrollToTop}>
                Shipping &amp; Delivery Policy
              </Link>{" "}
              and{" "}
              <Link className="content-page__inline-link" to="/international" onClick={scrollToTop}>
                International Orders Policy
              </Link>
              .
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="terms-returns">
            <h2 id="terms-returns">7. Returns, refunds and guarantee</h2>
            <p>
              Return eligibility can depend on timing, condition and food-safety rules.
              Do not mail a product back until customer care supplies instructions.
              Approved refunds are sent to the original payment method where possible.
              The voluntary Satisfaction Guarantee supplements rights that cannot be
              excluded by law.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="terms-safety">
            <h2 id="terms-safety">8. Product use and safety</h2>
            <p>
              Culinary products must be used and stored according to the label. Website
              content is general information, not medical, nutritional or professional
              advice. Customers with allergies or health concerns should review the
              product label and obtain appropriate professional advice before use.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="terms-communications">
            <h2 id="terms-communications">9. Electronic communications</h2>
            <p>
              When you contact us or provide an email for checkout, you agree that order,
              policy, safety and customer-service communications may be provided
              electronically where permitted by law. Marketing messages, if ever offered,
              will use the choices and unsubscribe method required by applicable law.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="terms-license">
            <h2 id="terms-license">10. Limited site permission</h2>
            <p>
              sickzuno gives you a limited, revocable, non-transferable permission to use
              the storefront for personal, lawful shopping. This does not transfer any
              ownership right or permit commercial copying, deceptive framing, bulk
              extraction, resale of site content or misuse of brand assets.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="terms-conduct">
            <h2 id="terms-conduct">11. Prohibited conduct</h2>
            <ul className="content-page__list">
              <li>Do not commit payment, promotion, return or chargeback fraud.</li>
              <li>Do not interfere with the site or attempt unauthorized access.</li>
              <li>Do not submit malware, unlawful content or another person’s data without authority.</li>
              <li>Do not impersonate sickzuno or imply an unauthorized endorsement.</li>
              <li>Do not infringe copyright, trademark, privacy or other legal rights.</li>
            </ul>
          </section>

          <section className="content-page__section" aria-labelledby="terms-third-parties">
            <h2 id="terms-third-parties">12. Third-party services and links</h2>
            <p>
              Payment, carrier and external-link services are operated by third parties.
              Their availability and conduct are subject to their terms. sickzuno is not
              responsible for third-party content or practices it does not control, but
              this does not exclude responsibility that applicable law places on us.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="terms-disclaimer">
            <h2 id="terms-disclaimer">13. Service availability and warranties</h2>
            <p>
              We work to keep the storefront accurate and available, but maintenance,
              network problems or errors may interrupt it. To the extent allowed by law,
              the site is provided without a promise that it will always be uninterrupted,
              error-free or compatible with every device. Express product promises and
              mandatory consumer warranties remain unaffected.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="terms-liability">
            <h2 id="terms-liability">14. Responsibility</h2>
            <p>
              Each party remains responsible for loss it causes under applicable law.
              To the extent the law permits, sickzuno is not responsible for indirect or
              unforeseeable loss caused by misuse, unauthorized access by the customer,
              a prohibited use, or a third-party event outside reasonable control. No
              limitation applies where liability cannot lawfully be limited.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="terms-disputes">
            <h2 id="terms-disputes">15. Complaints and applicable law</h2>
            <p>
              Email harv@sickzuno.online first so the issue can be reviewed under the{" "}
              <Link className="content-page__inline-link" to="/complaints" onClick={scrollToTop}>
                Complaints &amp; Disputes Policy
              </Link>
              . Applicable law governs these terms without removing any mandatory
              protection or forum available to a consumer in their place of residence.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="terms-general">
            <h2 id="terms-general">16. Changes and general terms</h2>
            <p>
              We may update these terms prospectively by posting a revised version and
              changing the date above. The terms in effect when an order is accepted
              generally govern that order unless law requires otherwise. If one provision
              is unenforceable, the remaining provisions continue to apply to the extent
              possible. A delay in enforcing a term is not a waiver.
            </p>
          </section>

          <aside className="content-page__support" aria-label="Terms support">
            <div>
              <h2>Questions about these terms?</h2>
              <p>Email customer care at harv@sickzuno.online.</p>
            </div>
            <Link className="content-page__inline-link" to="/policies" onClick={scrollToTop}>
              View all policies
            </Link>
          </aside>
        </article>
      </div>
    </main>
  );
}

export default Terms;
